import type React from "react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Project } from "senf-shared/types";
import { getFileTypeLabel } from "senf-shared/utils/helpers";

import { useImageMetaData } from "../../../hooks/useImageMetaData";
import { cn } from "../../../util/classNameConstructor";
import Button, { type ButtonProps } from "../../core/button/Button";
import Dropdown from "../../core/dropdown/Dropdown";
import Icon from "../../core/icon/Icon";
import { Input } from "../../core/inputs/Input";
import Radio from "../../core/radio/Radio";
import { useModals } from "../../molecules/modalStack/ModalProvider";
import type { SurveyImage } from "../survey/survey.config";
import type { ImageInput } from "./ImagesUploadModal";
import ImagesUploadModal from "./ImagesUploadModal";

interface ModifyImageButtonsProps {
  inputRef: React.RefObject<HTMLInputElement | null>;
  imageUrl?: string;
  altText?: string;
  altTextPlaceholder?: string;
  copyright?: string;
  onChangeImage?: (filesOrImages: ImageInput[]) => Promise<void> | undefined;
  onRemoveImage?: () => void;
  onAltTextChange?: (value: string) => void;
  onCopyrightChange?: (value: string) => void;
  onFormatChange?: (value: boolean) => void;
  imgFormat?: boolean;
  showFormat?: boolean;
  className?: string;
  classNames?: {
    trigger?: string;
  };
  variant?: ButtonProps["variant"];
  maxItems?: number;
  project?: Project;
  surveyId?: string;
  type?: "image" | "video";
  fileTypes?: string[];
  allImages?: SurveyImage[];
  fileTypeLabel?: {
    singular: string;
    plural: string;
  };
  saveToPath?: string;
}

const AltTextInput = ({
  altText,
  altTextPlaceholder,
  onAltTextChange,
  isAnalyzing,
  className,
}: {
  altText: string | undefined;
  altTextPlaceholder?: string;
  onAltTextChange: (value: string) => void;
  isAnalyzing?: boolean;
  className?: string;
}) => {
  const { t } = useTranslation();

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {isAnalyzing ? (
        <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
          <Icon name="loading" />
          <span className="body-sm text-text-subdued">
            {t("util:isBeingGenerated", {
              val: t("global:altText"),
            })}
            ...
          </span>
        </div>
      ) : (
        <Input
          label={t("global:altText")}
          labelOutside
          trailingIcon="info-circle"
          trailingIconProps={{
            label: t("global:whatIsAltDescription"),
            onClick: "openTooltip",
          }}
          value={altText || ""}
          type="textarea"
          onChange={({ currentTarget }) =>
            onAltTextChange?.(currentTarget.value)
          }
          placeholder={
            altTextPlaceholder ||
            t("util:input", {
              val: t("global:altText"),
            })
          }
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
          rows={3}
          maxRows={6}
        />
      )}
    </div>
  );
};

const CopyrightTextInput = ({
  copyright,
  onCopyrightChange,
  className,
}: {
  copyright: string | undefined;
  onCopyrightChange: (value: string) => void;
  className?: string;
}) => {
  const { t } = useTranslation();
  return (
    <div className={className}>
      <Input
        label={t("global:copyright")}
        labelOutside
        value={copyright || ""}
        onChange={({ currentTarget }) =>
          onCopyrightChange?.(currentTarget.value)
        }
        prepend="©"
        placeholder={t("global:copyrightPlaceholder")}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
      />
    </div>
  );
};

const ModifyImageButtons = ({
  project,
  imageUrl,
  altText,
  // altTextPlaceholder,
  copyright,
  onChangeImage,
  onRemoveImage,
  onAltTextChange,
  onCopyrightChange,
  onFormatChange,
  imgFormat,
  showFormat = false,
  className,
  classNames,
  maxItems,
  fileTypes,
  fileTypeLabel,
  saveToPath,
  variant,
}: ModifyImageButtonsProps) => {
  const {
    metadata: imageMetadata,
    loading,
    fetchMetadata,
  } = useImageMetaData({
    images: [{ url: imageUrl }],
  });
  const { t } = useTranslation();
  const { openModal } = useModals();
  const abortControllerRef = useRef<AbortController>(new AbortController());

  const abort = (reason?: string) => {
    abortControllerRef.current?.abort(reason);
    abortControllerRef.current = new AbortController();
  };

  const handleRemoveImage = () => {
    abort("image deleted");
    onRemoveImage?.();
  };

  const [open, setOpen] = useState(false);

  if (!imageUrl) return null;

  return (
    <div
      className={cn(
        "flex gap-1 absolute end-2 top-2 opacity-0 group-hover/option:opacity-100 [&:has([data-state='open'])]:!opacity-100",
        className,
      )}
    >
      <Dropdown
        size="sm"
        classNames={{
          contentWrapper:
            onAltTextChange || onCopyrightChange ? "w-[320px]" : "",
        }}
        trailingIcon="more"
        onClick={() => {
          fetchMetadata();
          setOpen(true);
        }}
        open={open}
        variant={variant}
        className={classNames?.trigger}
        items={[
          onAltTextChange || onCopyrightChange || onChangeImage || showFormat
            ? {
                children: (
                  <div
                    className={cn("flex flex-col -m-1")}
                    // onClick={(e) => e.stopPropagation()}
                  >
                    {onAltTextChange && (
                      <AltTextInput
                        altText={altText}
                        altTextPlaceholder={
                          imageMetadata[imageUrl]?.customMetadata?.alt
                        }
                        onAltTextChange={onAltTextChange}
                        loading={loading}
                        className="px-5 py-4 border-b border-border-subdued"
                      />
                    )}
                    {onCopyrightChange && (
                      <CopyrightTextInput
                        copyright={copyright}
                        onCopyrightChange={onCopyrightChange}
                        className="px-5 py-4 border-b border-border-subdued"
                      />
                    )}
                    {showFormat && (
                      <Radio
                        name="imgFormat"
                        elements={[
                          {
                            value: "fill",
                            label: t("global:imageFormat.fill"),
                          },
                          {
                            value: "fit",
                            label: t("global:imageFormat.fit"),
                          },
                        ]}
                        label={t("global:imageFormat")}
                        value={imgFormat ? "fill" : "fit"}
                        onValueChange={(e) => onFormatChange?.(e === "fill")}
                        className="min-h-0 items-center px-5 py-4 border-b border-border-subdued"
                      />
                    )}
                    <div className="px-5 py-4 flex gap-2">
                      <Button
                        leadingIcon="image"
                        text={t("util:change", {
                          val: t(getFileTypeLabel(1, fileTypes, fileTypeLabel)),
                        })}
                        width="max"
                        onClick={() => {
                          setOpen(false);
                          openModal(
                            <ImagesUploadModal
                              onInputChange={async (filesOrImages) => {
                                abort("images changed");
                                await onChangeImage?.(filesOrImages);
                              }}
                              maxItems={maxItems}
                              fileTypes={fileTypes}
                              stock
                              ai
                              mediaBrowser
                              currentImageUrl={imageUrl}
                              basePath={
                                project
                                  ? `organizationsData/${project.organizationId}/projects/${project.projectId}`
                                  : ""
                              }
                              saveToPath={saveToPath}
                              project={project}
                            />,
                            {
                              title: t("util:change", {
                                val: t(
                                  getFileTypeLabel(1, fileTypes, fileTypeLabel),
                                ),
                              }),
                              size: "xl",
                              transform: false,
                              style: { height: "80vh" },
                            },
                          );
                        }}
                      />
                      <Button
                        leadingIcon="trash"
                        critical
                        onClick={handleRemoveImage}
                        tooltipLabel={t("util:delete", {
                          val: t(getFileTypeLabel(1, fileTypes, fileTypeLabel)),
                        })}
                      />
                    </div>
                  </div>
                ),
                closeOnClick: false,
              }
            : null,
        ].filter((item) => item !== null)}
      />
    </div>
  );
};

ModifyImageButtons.displayName = "ModifyImageButtons";

export default ModifyImageButtons;
